"use client";

import Link from "next/link";
import { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { classNames } from "../ui/class-names";
import {
  markdownFileAccept,
  markdownFileExtensions,
  readMarkdownFile,
  type ReadMarkdownFileSuccess,
} from "../editor/read-markdown-file";

export function MarkdownFileDropzone({
  onLoaded,
  onStartBlank,
}: Readonly<{
  onLoaded: (result: ReadMarkdownFileSuccess) => void;
  onStartBlank: () => void;
}>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isReading, setIsReading] = useState(false);

  async function loadFile(file: File | undefined) {
    if (!file || isReading) {
      return;
    }

    setIsReading(true);
    setErrorMessage(null);

    try {
      const result = await readMarkdownFile(file);
      if (!result.ok) {
        setErrorMessage(result.error);
        return;
      }
      onLoaded(result);
    } finally {
      setIsReading(false);
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-6 px-4 py-10 sm:py-16">
      <div className="text-center">
        <h1 className="text-xl font-semibold text-zinc-950 sm:text-2xl">
          Open a Markdown file
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-zinc-600">
          Your file stays in the browser. Edit with a live preview, then
          download a PDF. Nothing is uploaded until you choose Download.
        </p>
      </div>

      <div
        role="button"
        tabIndex={0}
        aria-label="Drop a Markdown file here, or press Enter to choose a file"
        aria-disabled={isReading}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (!isReading) {
              inputRef.current?.click();
            }
          }
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsDragging(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsDragging(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsDragging(false);
          void loadFile(event.dataTransfer.files?.[0]);
        }}
        onClick={() => {
          if (!isReading) {
            inputRef.current?.click();
          }
        }}
        className={classNames(
          "flex cursor-pointer flex-col items-center gap-3 rounded-lg border-2 border-dashed px-6 py-12 text-center transition outline-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
          isDragging
            ? "border-brand bg-brand/5"
            : "border-zinc-300 bg-white hover:border-zinc-400 hover:bg-zinc-50",
          isReading && "pointer-events-none opacity-70",
        )}
      >
        <Upload
          aria-hidden="true"
          className="size-8 text-zinc-500"
          strokeWidth={1.5}
        />
        <div className="space-y-1">
          <p className="text-sm font-medium text-zinc-950">
            {isReading
              ? "Reading file…"
              : "Drop a Markdown file here, or click to browse"}
          </p>
          <p className="text-xs text-zinc-500">
            Accepts {markdownFileExtensions.join(", ")} · max 2 MiB
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={markdownFileAccept}
          className="sr-only"
          tabIndex={-1}
          disabled={isReading}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            void loadFile(file);
          }}
        />
      </div>

      {errorMessage ? (
        <p
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-center text-sm text-red-700"
        >
          {errorMessage}
        </p>
      ) : null}

      <div className="flex flex-col items-center gap-3 text-sm text-zinc-600 sm:flex-row sm:justify-center sm:gap-4">
        <button
          type="button"
          onClick={onStartBlank}
          className="font-medium text-brand outline-brand hover:text-brand-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          Start with a blank editor
        </button>
        <span aria-hidden="true" className="hidden text-zinc-300 sm:inline">
          ·
        </span>
        <Link
          href="/markdown-editor"
          className="outline-brand hover:text-zinc-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          Open full-screen editor
        </Link>
      </div>
    </div>
  );
}
